import React, { useContext, useEffect, useState } from "react";
import { View, StyleSheet, ScrollView, Modal, Pressable } from "react-native";
import {
  NormalText,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../../common/native";

import { SessionContext } from "../../../../contexts";
import { ModalOpener } from "../../../uncommon/native/modals";
import { TPlan } from "../../../../utils/types";
import {
  patchUpdatePlan,
  createBillingPortalSession,
  getClinicPlan,
  TCommunicationsPlanPricingItem,
  getPlanPricing,
  TPlanPricingItem,
} from "../../../../services/api";
import { logUIError } from "../../../../services/api-misc";
import { mixpanel } from "../../../../utils/mixpanel";
import StripCheckoutForm from "./StripeCheckoutForm";

type UpgradeProps = {};
const SHOW_PREMIUM_PLAN = false; // Intentionally hidden for now. Keep Premium logic in place for a future release.

const formatCurrencyFromCents = (
  cents: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (typeof cents !== "number" || !Number.isFinite(cents)) {
    return "-";
  }

  const normalizedCurrency = (currency || "USD").trim().toUpperCase() || "USD";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isHttpErrorMessage = (message: string | null): boolean => {
  if (!message) {
    return false;
  }
  return /http error/i.test(message);
};

const Upgrade: React.FC<UpgradeProps> = ({}) => {
  const session = useContext(SessionContext)?.session;

  const [entryButtonLoading, setEntryButtonLoading] = useState<boolean>(false);
  const [coreButtonLoading, setCoreButtonLoading] = useState<boolean>(false);

  const [isPlanUpdated, setIsPlanUpdated] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [entryPlanButtonText, setEntryPlanButtonText] =
    useState<string>("Upgrade");
  const [corePlanButtonText, setCorePlanButtonText] =
    useState<string>("Upgrade");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [planToUpgrade, setPlanToUpgrade] = useState<TPlan | null>(null);
  const [isPaymentDelinquent, setIsPaymentDelinquent] =
    useState<boolean>(false);
  const [communicationsPricing, setCommunicationsPricing] =
    useState<TCommunicationsPlanPricingItem | null>(null);
  const [communicationsPricingError, setCommunicationsPricingError] = useState<
    string | null
  >(null);
  const [planPricingByPlan, setPlanPricingByPlan] = useState<
    Partial<Record<TPlan, TPlanPricingItem>>
  >({});
  const [planPricingLoading, setPlanPricingLoading] = useState<boolean>(true);
  const [planPricingError, setPlanPricingError] = useState<string | null>(null);
  const [isExtrasModalOpen, setIsExtrasModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!session) console.error("Session not found");

    setEntryPlanButtonText(
      session?.planType === "FREE"
        ? "Upgrade"
        : session?.planType === "ENTRY"
          ? "Current Plan"
          : "Downgrade",
    );

    setCorePlanButtonText(
      session?.planType === "FREE" || session?.planType === "ENTRY"
        ? "Upgrade"
        : session?.planType === "CORE"
          ? "Current Plan"
          : "Downgrade",
    );

    // Fetch clinic billing/payment info to determine delinquency
    (async () => {
      try {
        if (!session) return;
        const planInfo = await getClinicPlan(session);
        console.log("Fetched clinic plan info:", planInfo);
        console.log("is_payment_delinquent:", planInfo?.is_payment_delinquent);
        setIsPaymentDelinquent(Boolean(planInfo?.is_payment_delinquent));
      } catch (e) {
        console.error("Could not fetch clinic billing info", e);
        logUIError({
          message: "Failed to fetch clinic billing info",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: session?.userEmail || undefined,
          clinic_name: session?.clinicName || undefined,
          clinic_id: session?.clinicId || undefined,
          context: {
            component: "HomeUpgrade",
            action: "getClinicPlan",
            error: e instanceof Error ? e.message : String(e),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in HomeUpgrade", logErr),
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!session) {
      return;
    }

    let isMounted = true;
    const abortController = new AbortController();

    const loadPlanPricing = async () => {
      setPlanPricingLoading(true);
      setPlanPricingError(null);

      try {
        const response = await getPlanPricing(session, {
          signal: abortController.signal,
        });

        if (!isMounted) {
          return;
        }

        const byPlan: Partial<Record<TPlan, TPlanPricingItem>> = {};
        response.prices.forEach((item) => {
          byPlan[item.plan] = item;
        });
        setPlanPricingByPlan(byPlan);
        setCommunicationsPricing(response.communications_pricing || null);
        setCommunicationsPricingError(null);
      } catch (error) {
        if (!isMounted || abortController.signal.aborted) {
          return;
        }

        setPlanPricingByPlan({});
        setCommunicationsPricing(null);
        setCommunicationsPricingError(
          error instanceof Error
            ? error.message
            : "Unable to load communications details.",
        );
        setPlanPricingError(
          error instanceof Error
            ? error.message
            : "Unable to load plan pricing right now.",
        );
      } finally {
        if (isMounted) {
          setPlanPricingLoading(false);
        }
      }
    };

    loadPlanPricing().catch(() => undefined);

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [session]);

  const [plan, setPlan] = useState<TPlan | null>(null);

  if (plan) {
    return <StripCheckoutForm plan={plan} />;
  }

  const handleOnPressButton = async (
    planButton: TPlan,
    planButtonCurrentText: string,
  ) => {
    if (!session) return;
    // If payment delinquent, send user to billing portal first
    if (isPaymentDelinquent) {
      try {
        const data = await createBillingPortalSession(session);
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } catch (e) {
        console.error("Failed to open billing portal", e);
        alert("Unable to open billing settings. Please try again.");
        return;
      }
    }

    // Don't allow to upgrade to the same plan
    if (planButtonCurrentText === "Current Plan") return;

    // Show payment form if the user is on the free plan
    mixpanel.track("Package Selected", {
      "User Email": session.userEmail,
      "Clinic Name": session.clinicName,
      Package: planButton,
    });
    if (session?.planType === "FREE") {
      setPlan(planButton);
      return;
    }

    // Show modal to confirm downgrade
    if (planButtonCurrentText === "Downgrade") {
      setPlanToUpgrade(planButton);
      setIsModalOpen(true);
    }

    // Upgrade to a plan
    if (planButtonCurrentText === "Upgrade") {
      if (planButton === "ENTRY") setEntryButtonLoading(true);
      if (planButton === "CORE") setCoreButtonLoading(true);
      try {
        if (!session) throw new Error("Session not found");
        await patchUpdatePlan(session, planButton);
        setIsPlanUpdated(true);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } catch (error) {
        setIsError(true);
      }
      if (planButton === "ENTRY") setEntryButtonLoading(false);
      if (planButton === "CORE") setCoreButtonLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPlanToUpgrade(null);
  };

  const renderCardItems = (texts: string[]) => {
    return texts.map((text, index) => (
      <NormalText key={index} text={text} style={{ marginBottom: 16 }} />
    ));
  };

  const formatPlanPrice = (planType: TPlan): string | null => {
    if (planPricingLoading) {
      return "Loading price...";
    }
    if (planPricingError) {
      return null;
    }

    const item = planPricingByPlan[planType];
    if (!item || typeof item.unit_amount !== "number") {
      return null;
    }

    const intervalLabel = item.interval
      ? item.interval_count && item.interval_count > 1
        ? `${item.interval_count} ${item.interval}s`
        : item.interval
      : "month";

    return `${formatCurrencyFromCents(item.unit_amount, item.currency)} / ${intervalLabel}`;
  };
  const formatPremiumPrice = (): string | null => {
    if (planPricingLoading) {
      return "Loading price...";
    }
    if (planPricingError) {
      return null;
    }

    const coreItem = planPricingByPlan.CORE;
    if (!coreItem || typeof coreItem.unit_amount !== "number") {
      return null;
    }

    const communicationsMonthlyPriceCents =
      communicationsPricing?.monthly_price_cents;
    if (!isFiniteNumber(communicationsMonthlyPriceCents)) {
      return null;
    }

    const intervalLabel = coreItem.interval
      ? coreItem.interval_count && coreItem.interval_count > 1
        ? `${coreItem.interval_count} ${coreItem.interval}s`
        : coreItem.interval
      : "month";

    return `${formatCurrencyFromCents(
      coreItem.unit_amount + communicationsMonthlyPriceCents,
      communicationsPricing?.currency || coreItem.currency,
    )} / ${intervalLabel}`;
  };

  if (!session) return null;

  if (isError)
    return (
      <View style={styles.messageContainer}>
        <NormalText text={"Something went wrong, please try again later"} />
      </View>
    );

  if (isPlanUpdated)
    return (
      <View style={styles.messageContainer}>
        <NormalText text={"Plan updated! Refresh in 3 second!"} />
      </View>
    );

  const entryPrice = formatPlanPrice("ENTRY");
  const corePrice = formatPlanPrice("CORE");
  const premiumPrice = formatPremiumPrice();

  return (
    <View style={styles.upgradeContainer}>
      <ModalOpener
        visible={isModalOpen}
        modalType={"ConfirmUpdatePlan"}
        onClose={closeModal}
        planToUpgrade={planToUpgrade}
      />
      <Modal
        animationType="fade"
        transparent
        visible={isExtrasModalOpen}
        onRequestClose={() => setIsExtrasModalOpen(false)}
      >
        <Pressable
          style={styles.extrasModalBackdrop}
          onPress={() => setIsExtrasModalOpen(false)}
        >
          <Pressable style={styles.extrasModalView} onPress={() => {}}>
            <TitleText
              text="Premium extras"
              fontWeight="700"
              style={{ marginBottom: 16 }}
            />
            <NormalText text="Optional add-ons:" style={{ marginBottom: 16 }} />
            {isFiniteNumber(
              communicationsPricing?.user_seat_monthly_price_cents,
            ) ? (
              <NormalText
                text={`Additional seats: ${formatCurrencyFromCents(
                  communicationsPricing?.user_seat_monthly_price_cents,
                  communicationsPricing?.currency ?? "USD",
                )}/seat/month`}
                style={{ marginBottom: 12 }}
              />
            ) : null}
            {isFiniteNumber(
              communicationsPricing?.extra_number_monthly_price_cents,
            ) ? (
              <NormalText
                text={`Additional numbers: ${formatCurrencyFromCents(
                  communicationsPricing?.extra_number_monthly_price_cents,
                  communicationsPricing?.currency ?? "USD",
                )}/number/month`}
                style={{ marginBottom: 12 }}
              />
            ) : null}
            {isFiniteNumber(
              communicationsPricing?.overage_minute_price_cents,
            ) ? (
              <NormalText
                text={`Overage minutes: ${formatCurrencyFromCents(
                  communicationsPricing?.overage_minute_price_cents,
                  communicationsPricing?.currency ?? "USD",
                )}/minute`}
                style={{ marginBottom: 24 }}
              />
            ) : null}
            <PrimaryButton
              text="Close"
              onPress={() => setIsExtrasModalOpen(false)}
              roundBorderRadius
            />
          </Pressable>
        </Pressable>
      </Modal>
      <ScrollView
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        style={{ width: "100%" }}
      >
        {/* Delinquent payment banner */}
        {isPaymentDelinquent && (
          <View
            style={{
              padding: 12,
              backgroundColor: "#ffe6e6",
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <NormalText text="Your last payment failed. Please update your payment method to avoid service interruption." />
            <PrimaryButton
              text="Update Payment Method"
              onPress={async () => {
                try {
                  const data = await createBillingPortalSession(session);
                  if (data.url) {
                    window.location.href = data.url;
                  }
                } catch (e) {
                  console.error(e);
                  alert("Unable to open billing settings. Please try again.");
                }
              }}
              style={{ marginTop: 12 }}
              roundBorderRadius
            />
          </View>
        )}

        <TitleText
          text={"Upgrade"}
          style={{ marginTop: 5, marginBottom: 20 }}
        />

        {session.planType !== "FREE" && (
          <View
            style={{ width: "100%", alignItems: "flex-end", marginTop: 20 }}
          >
            <SecondaryButton
              text="Change Payment Method"
              onPress={async () => {
                try {
                  const data = await createBillingPortalSession(session);
                  if (data?.url) {
                    window.location.href = data.url;
                  }
                } catch (e) {
                  console.error("Failed to open billing portal:", e);
                  alert("Unable to open billing settings. Please try again.");
                }
              }}
              roundBorderRadius
            />
          </View>
        )}

        <View style={styles.cardsContainer}>
          <View style={styles.cardContainer}>
            <TitleText text={"Entry"} fontWeight="700" />
            <NormalText text={entryPrice ?? ""} style={{ marginBottom: 36 }} />
            {renderCardItems([
              "Unlimited AI simulations",
              "Unlimited voice notes",
              "Unlimited before/afters",
              "All procedures",
              "iPad & iPhone app",
              "Email support",
            ])}
            <View style={{ marginTop: "auto" }} />
            <SecondaryButton
              text={entryPlanButtonText}
              loading={entryButtonLoading}
              onPress={() => handleOnPressButton("ENTRY", entryPlanButtonText)}
              roundBorderRadius
            />
          </View>

          <View style={{ width: "100%", maxWidth: 347 }}>
            <View style={styles.mostPopularContainer}>
              <TitleText
                text={"Most Popular"}
                style={{ color: "#fff" }}
                fontWeight="700"
              />
            </View>
            <View
              style={[
                styles.cardContainer,
                { borderTopStartRadius: 0, borderTopEndRadius: 0 },
              ]}
            >
              <TitleText text={"Core"} fontWeight="700" />
              <NormalText text={corePrice ?? ""} style={{ marginBottom: 36 }} />
              {renderCardItems([
                "Everything in Entry",
                "Full EMR integration",
                "Photo management",
                "Semantic search",
                "Priority support",
              ])}
              <View style={{ marginTop: "auto" }} />
              <PrimaryButton
                text={corePlanButtonText}
                loading={coreButtonLoading}
                onPress={() => handleOnPressButton("CORE", corePlanButtonText)}
                roundBorderRadius
              />
            </View>
          </View>

          {SHOW_PREMIUM_PLAN ? (
            <View style={styles.cardContainer}>
              <TitleText text={"Premium"} fontWeight="700" />
              <NormalText
                text={premiumPrice ?? ""}
                style={{ marginBottom: 36 }}
              />
              {renderCardItems([
                "Everything in Core, plus...",
                "Call and SMS platform",
                "AI Transcribe & Summarize calls",
                "AI messaging assistant",
                `Included: ${communicationsPricing?.included_number_count ?? 1} number & ${communicationsPricing?.included_minutes ?? 500} minutes`,
              ])}
              <Pressable
                onPress={() => setIsExtrasModalOpen(true)}
                style={{ marginBottom: 16 }}
              >
                <NormalText
                  text="See pricing for extras"
                  style={{ color: "#00B7F4", textDecorationLine: "underline" }}
                />
              </Pressable>
              {communicationsPricingError &&
              !isHttpErrorMessage(communicationsPricingError) ? (
                <NormalText
                  text={communicationsPricingError}
                  style={{ marginBottom: 16 }}
                />
              ) : null}
              <View style={{ marginTop: "auto" }} />
              <SecondaryButton
                text={"Contact Us"}
                onPress={() => {
                  mixpanel.track("Upgrade Contact Us Clicked", {
                    Source: "HomeUpgrade",
                    Package: "PREMIUM",
                    "User Email": session.userEmail,
                    "Clinic Name": session.clinicName,
                  });
                  mixpanel.track("Package Selected", {
                    "User Email": session.userEmail,
                    "Clinic Name": session.clinicName,
                    Package: "PREMIUM",
                  });
                  window.open(
                    "https://meetings.hubspot.com/kav-ohiomoba?uuid=000a6c6c-4e10-4fc7-876d-7907cc133fb6",
                    "_blank",
                  );
                }}
                roundBorderRadius
              />
            </View>
          ) : null}

          <View style={styles.cardContainer}>
            <TitleText text={"Enterprise"} fontWeight="700" />
            <NormalText text={"Custom pricing"} style={{ marginBottom: 36 }} />
            {renderCardItems([
              "Everything in Core",
              "Multi-location support",
              "API access",
              "Custom integrations",
              "Dedicated account manager",
              "Custom onboarding",
            ])}
            <View style={{ marginTop: "auto" }} />
            <SecondaryButton
              text={"Contact Us"}
              onPress={() => {
                mixpanel.track("Upgrade Contact Us Clicked", {
                  Source: "HomeUpgrade",
                  Package: "ENTERPRISE",
                  "User Email": session.userEmail,
                  "Clinic Name": session.clinicName,
                });
                mixpanel.track("Package Selected", {
                  "User Email": session.userEmail,
                  "Clinic Name": session.clinicName,
                  Package: "ENTERPRISE",
                });
                window.open("https://clinicos.ai/contact", "_blank");
              }}
              roundBorderRadius
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  upgradeContainer: {
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  cardsContainer: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-end",
    gap: 20,
    paddingBottom: 20,
  },
  cardContainer: {
    width: "100%",
    maxWidth: 347,
    minHeight: 469,
    padding: 36,
    borderRadius: 20,
    backgroundColor: "#fff",
    borderColor: "rgba(233, 233, 233, 0.60)",
    borderWidth: 1,
    shadowColor: "#00B7F4",
    shadowOffset: { width: 10, height: 10 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 5,
  },
  mostPopularContainer: {
    width: "100%",
    maxWidth: 347,
    backgroundColor: "#00B7F4",
    paddingVertical: 5,
    justifyContent: "center",
    alignItems: "center",
    borderStartStartRadius: 20,
    borderStartEndRadius: 20,
  },
  messageContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  extrasModalBackdrop: {
    flex: 1,
    padding: 16,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  extrasModalView: {
    width: "100%",
    maxWidth: 465,
    padding: 24,
    borderRadius: 10,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
});

export default Upgrade;
